import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Users,
  CheckSquare,
  Building,
  Cpu,
  TrendingUp,
  Megaphone,
  AlertTriangle,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const AdminOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Heatmap Selector States
  const [heatmapDays, setHeatmapDays] = useState(7);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  const fetchOverview = async (showRefreshIndicator = false, days = heatmapDays) => {
    if (showRefreshIndicator) setRefreshing(true);
    try {
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${API_BASE_URL}/api/admin/analytics?days=${days}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load overview analytics.');
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to the API. Displaying simulation dashboard data.');
      // Load fallback simulation data to ensure visually excellent presentation
      setData(getMockData(days));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePeriodChange = async (newDays) => {
    setHeatmapDays(newDays);
    setHeatmapLoading(true);
    await fetchOverview(false, newDays);
    setHeatmapLoading(false);
  };

  const getMockData = (days = 7) => {
    const today = new Date();
    const dates = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      dates.push(d.toISOString().slice(0, 10)); // yyyy-MM-dd
    }

    const mockGyms = [
      { gymName: 'Muscle Factory, Hyderabad', gymCode: 'MUS-1029', countsBase: [15, 20, 22, 18, 25, 30, 28] },
      { gymName: 'Iron House, Vijayawada', gymCode: 'IRO-8839', countsBase: [12, 14, 0, 10, 8, 15, 0] },
      { gymName: 'FitZone, Guntur', gymCode: 'FIT-3829', countsBase: [0, 0, 0, 0, 0, 0, 0] }, // Drop off alert
      { gymName: 'Gold Gym, Vizag', gymCode: 'GOL-2234', countsBase: [28, 32, 35, 30, 42, 48, 45] },
      { gymName: 'Reddy Fitness, Nellore', gymCode: 'RED-9923', countsBase: [8, 10, 12, 9, 11, 14, 10] }
    ];

    const gyms = mockGyms.map(gym => {
      const counts = [];
      const isZero = gym.gymCode === 'FIT-3829';
      for (let i = 0; i < days; i++) {
        if (isZero) {
          counts.push(0);
        } else {
          const baseIndex = i % gym.countsBase.length;
          counts.push(gym.countsBase[baseIndex]);
        }
      }
      return {
        gymName: gym.gymName,
        gymCode: gym.gymCode,
        counts
      };
    });

    return {
      kpis: {
        checkInsToday: 342,
        activeGymsThisWeek: 8,
        totalMembers: 1240,
        activeChallenges: 580,
        newGymsThisMonth: 3,
        adImpressionsToday: 712
      },
      heatmap: {
        dates,
        gyms
      },
      recentActivities: [
        { id: '1', adminEmail: 'admin@fitscan.in', action: 'GYM_ONBOARDED', targetId: '1', details: "Onboarded 'Reddy Fitness, Nellore'", occurredAt: new Date(Date.now() - 3600000).toISOString() },
        { id: '2', adminEmail: 'admin@fitscan.in', action: 'GYM_SUSPENDED', targetId: '2', details: "Suspended 'FitZone, Guntur' due to inactive device", occurredAt: new Date(Date.now() - 7200000).toISOString() },
        { id: '3', adminEmail: 'admin@fitscan.in', action: 'GYM_EDITED', targetId: '3', details: "Updated PlanTier to 'Pro' for 'Gold Gym, Vizag'", occurredAt: new Date(Date.now() - 86400000).toISOString() }
      ]
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 text-[#6CD800]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#6CD800]"></div>
        <span className="ml-3 font-semibold">Loading platform metrics...</span>
      </div>
    );
  }

  // Derive drop-off alerts from the heatmap/devices data
  const dropOffAlerts = [];
  if (data?.heatmap?.gyms) {
    data.heatmap.gyms.forEach(gym => {
      // Alert if check-ins for the entire last week is 0
      const totalCheckIns = gym.counts.slice(-7).reduce((sum, c) => sum + c, 0); // Always evaluate trailing 7 days for alerts
      if (totalCheckIns === 0) {
        dropOffAlerts.push({
          gymName: gym.gymName,
          reason: 'Zero check-ins recorded in the last 7 days',
          severity: 'high'
        });
      } else if (gym.counts[gym.counts.length - 1] === 0 && gym.counts[gym.counts.length - 2] === 0) {
        dropOffAlerts.push({
          gymName: gym.gymName,
          reason: 'Consecutive drop-off in check-ins over the last 48 hours',
          severity: 'medium'
        });
      }
    });
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">System Overview</h1>
          <p className="text-gray-400 text-sm mt-1">Real-time health indices and operations dashboard.</p>
        </div>
        <button
          onClick={() => fetchOverview(true)}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] rounded-lg text-sm text-gray-300 transition-all hover:text-white"
        >
          <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin text-[#6CD800]' : ''}`} />
          Refresh Data
        </button>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Card 1: Checkins Today */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Check-ins Today</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><CheckSquare size={20} /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-bold text-white">{data?.kpis?.checkInsToday}</span>
            <p className="text-xs text-gray-400 mt-1">Platform-wide scans</p>
          </div>
        </div>

        {/* Card 2: Active Gyms */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Active Gyms (Weekly)</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><Building size={20} /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-bold text-white">{data?.kpis?.activeGymsThisWeek}</span>
            <p className="text-xs text-gray-400 mt-1">At least 1 check-in</p>
          </div>
        </div>

        {/* Card 3: Total Members */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Total Members</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><Users size={20} /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-bold text-white">{data?.kpis?.totalMembers}</span>
            <p className="text-xs text-gray-400 mt-1">Across all registered gyms</p>
          </div>
        </div>

        {/* Card 4: Active Challenges */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Challenges Active</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><TrendingUp size={20} /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-bold text-white">{data?.kpis?.activeChallenges}</span>
            <p className="text-xs text-gray-400 mt-1">100-Day challenges in progress</p>
          </div>
        </div>

        {/* Card 5: New Gyms */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">New Gyms (Month)</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><Cpu size={20} /></div>
          </div>
          <div className="mt-4">
            <span className="text-3xl font-bold text-white">{data?.kpis?.newGymsThisMonth}</span>
            <p className="text-xs text-gray-400 mt-1">Onboarded this calendar month</p>
          </div>
        </div>

        {/* Card 6: Ad Impressions */}
        <div className="bg-[#0F172A] border border-[#1E293B] hover:border-[#6CD800]/30 rounded-xl p-6 transition-all duration-300 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Ad Impressions Today</span>
            <div className="p-2 bg-[#6CD800]/10 rounded-lg text-[#6CD800]"><Megaphone size={20} /></div>
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <span className="text-3xl font-bold text-white">{data?.kpis?.adImpressionsToday}</span>
            <Link
              to="/admin/ads?view=impressions&range=daily"
              className="mt-1 text-center text-xs font-bold uppercase bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] py-2 px-3 rounded-lg transition-colors duration-200 inline-block shadow-md tracking-wider"
            >
              AD IMPRESSIONS TODAY
            </Link>
          </div>
        </div>
      </div>

      {/* Main Grid: Heatmap and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Heatmap Section */}
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg lg:col-span-2">
          {/* Heatmap Header Row */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">
              Gym Check-in Volume
            </h2>
            <select
              value={heatmapDays}
              onChange={e => handlePeriodChange(Number(e.target.value))}
              disabled={heatmapLoading}
              className="bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] text-gray-300 text-xs font-semibold rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#6CD800] cursor-pointer transition-colors disabled:opacity-50"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last Month</option>
              <option value={90}>Last 3 Months</option>
            </select>
          </div>

          {/* Loading Shimmer while switching periods */}
          {heatmapLoading && (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6CD800] mb-2"></div>
              <span>Loading heatmap data...</span>
            </div>
          )}

          {!heatmapLoading && (() => {
            const { labels, gymRows } = aggregateHeatmap(
              data?.heatmap?.dates ?? [],
              data?.heatmap?.gyms ?? [],
              heatmapDays
            );

            return (
              <div className="overflow-x-auto">
                <div className="min-w-[500px]">
                  {/* Column headers */}
                  <div
                    className="grid gap-2 mb-2 text-center text-xs font-bold text-gray-400"
                    style={{ gridTemplateColumns: `1.5fr repeat(${labels.length}, minmax(0, 1fr))` }}
                  >
                    <div className="text-left pl-2">Gym Name</div>
                    {labels.map((label, idx) => (
                      <div key={idx} className="truncate" title={label}>{label}</div>
                    ))}
                  </div>

                  {/* Gym Rows */}
                  <div className="space-y-2">
                    {gymRows.map((gym, gymIdx) => (
                      <div
                        key={gymIdx}
                        className="grid gap-2 items-center text-center py-2 hover:bg-[#1E293B]/20 rounded-lg"
                        style={{ gridTemplateColumns: `1.5fr repeat(${labels.length}, minmax(0, 1fr))` }}
                      >
                        <div className="text-left font-medium text-sm text-gray-300 truncate pl-2" title={gym.gymName}>
                          {gym.gymName.split(',')[0]}
                        </div>
                        {gym.displayCounts.map((count, ci) => {
                          const mid = heatmapDays === 7 ? 5 : heatmapDays === 30 ? 20 : 50;
                          const high = heatmapDays === 7 ? 15 : heatmapDays === 30 ? 60 : 150;
                          const isZeroGym = gym.displayCounts.every(c => c === 0);

                          let bgColor = 'bg-[#1E293B]';
                          let textColor = 'text-gray-500';
                          if (isZeroGym) {
                            bgColor = 'bg-red-500/10 border border-red-500/20';
                            textColor = 'text-red-400';
                          } else if (count > 0 && count <= mid) {
                            bgColor = 'bg-[#6CD800]/10 border border-[#6CD800]/25';
                            textColor = 'text-[#6CD800]';
                          } else if (count > mid && count <= high) {
                            bgColor = 'bg-[#6CD800]/30 border border-[#6CD800]/40';
                            textColor = 'text-white';
                          } else if (count > high) {
                            bgColor = 'bg-[#6CD800]';
                            textColor = 'text-[#0B0F19] font-bold';
                          }

                          return (
                            <div
                              key={ci}
                              title={`${gym.gymName}: ${count} check-ins`}
                              className={`h-9 flex items-center justify-center rounded-lg text-xs transition-transform hover:scale-105 cursor-pointer ${bgColor} ${textColor}`}
                            >
                              {count}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="flex items-center space-x-4 mt-6 text-xs text-gray-400 justify-end">
            <span className="flex items-center"><span className="h-3 w-3 bg-[#1E293B] rounded mr-1"></span> 0 check-ins</span>
            <span className="flex items-center"><span className="h-3 w-3 bg-[#6CD800]/10 rounded mr-1"></span> 1 - {heatmapDays === 7 ? 5 : heatmapDays === 30 ? 20 : 50}</span>
            <span className="flex items-center"><span className="h-3 w-3 bg-[#6CD800]/30 rounded mr-1"></span> {heatmapDays === 7 ? '6 - 15' : heatmapDays === 30 ? '21 - 60' : '51 - 150'}</span>
            <span className="flex items-center"><span className="h-3 w-3 bg-[#6CD800] rounded mr-1"></span> {heatmapDays === 7 ? '15+' : heatmapDays === 30 ? '60+' : '150+'} scans</span>
          </div>
        </div>

        {/* Drop-off Alerts Panel */}
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-white">Drop-Off Alerts</h2>
            {dropOffAlerts.length > 0 && (
              <span className="bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-semibold">
                {dropOffAlerts.length} Active
              </span>
            )}
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto max-h-[300px] pr-1">
            {dropOffAlerts.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">
                All systems active. No drop-off triggers flagged.
              </div>
            ) : (
              dropOffAlerts.map((alert, idx) => (
                <div key={idx} className="bg-[#1E293B]/30 border border-[#1E293B] hover:border-red-500/30 rounded-lg p-4 flex gap-3 transition-colors">
                  <div className="text-red-400 mt-0.5"><AlertTriangle size={18} /></div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-white truncate">{alert.gymName}</h4>
                    <p className="text-xs text-gray-400 mt-1">{alert.reason}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <Link to="/admin/devices" className="mt-4 flex items-center text-xs text-[#6CD800] font-semibold hover:underline">
            View Deployed Hardware <ArrowRight size={14} className="ml-1" />
          </Link>
        </div>
      </div>

      {/* Recent Activities Audit Feed */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-6 shadow-lg">
        <h2 className="text-lg font-bold text-white mb-4">Recent Audit Logs</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-300">
            <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
              <tr>
                <th scope="col" className="px-6 py-3">Operator</th>
                <th scope="col" className="px-6 py-3">Action</th>
                <th scope="col" className="px-6 py-3">Description</th>
                <th scope="col" className="px-6 py-3">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {data?.recentActivities?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-6 text-gray-500">No recent administrative activities logged.</td>
                </tr>
              ) : (
                data.recentActivities.map((log) => (
                  <tr key={log.id} className="hover:bg-[#1E293B]/20">
                    <td className="px-6 py-4 font-medium text-white">{log.adminEmail}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${log.action === 'GYM_SUSPENDED'
                        ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                        : log.action === 'GYM_ONBOARDED'
                          ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/25'
                          : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                        }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 max-w-xs truncate" title={log.details}>{log.details}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">{new Date(log.occurredAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Pure utility helper for grouping/aggregating heatmap data based on range selections
const aggregateHeatmap = (dates = [], gyms = [], days) => {
  if (days === 7) {
    // Daily - return format as-is
    return { labels: dates, gymRows: gyms.map(g => ({ ...g, displayCounts: g.counts })) };
  }

  if (days === 30) {
    // Group into ~5 weekly blocks
    const weekSize = 7;
    const weeks = [];
    for (let i = 0; i < dates.length; i += weekSize) {
      weeks.push(dates.slice(i, i + weekSize));
    }
    const labels = weeks.map(w => `w/c ${w[0].slice(5)}`); // e.g. "w/c 05-01"
    const gymRows = gyms.map(g => ({
      ...g,
      displayCounts: weeks.map((_, wi) =>
        g.counts.slice(wi * weekSize, wi * weekSize + weekSize)
          .reduce((a, b) => a + b, 0)
      )
    }));
    return { labels, gymRows };
  }

  if (days === 90) {
    // Group into 3 monthly blocks
    const dateObjs = dates.map(d => new Date(d));
    const months = [...new Set(dateObjs.map(d => `${d.getFullYear()}-${d.getMonth()}`))];
    const labels = months.map(m => {
      const [y, mo] = m.split('-');
      return new Date(y, mo).toLocaleString('default', { month: 'long' });
    });
    const gymRows = gyms.map(g => ({
      ...g,
      displayCounts: months.map(m => {
        const [y, mo] = m.split('-');
        return g.counts
          .filter((_, i) => {
            const d = dateObjs[i];
            return d.getFullYear() === +y && d.getMonth() === +mo;
          })
          .reduce((a, b) => a + b, 0);
      })
    }));
    return { labels, gymRows };
  }

  return { labels: [], gymRows: [] };
};

export default AdminOverview;
